import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Row, Spin, notification } from "antd";
import Icon, { LeftOutlined } from "@ant-design/icons";

import Header from "../../components/layout/header";
import {
  CustomSpin,
  LeftIcon,
  PriorityIcon,
  ToteIcon,
  TrolleyIcon,
} from "../../assets/icons/icons";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import PriorityConfirmationModal from "../../components/mobile/PriorityConfirmationModal";
import { getTrolleyStatus } from "../../components/reusable/styles";
import {
  useToteById,
  useTotePriorities,
  useTrolleyById,
} from "../common/queries";
import { useUpdateTotePriority } from "../common/mutations";
import { api } from "../../services/api";

import Styles from "./supervisorToteInfo.module.scss";
import { convertUTCToLocal } from "../../utils/timeUtils";

const ALLOWED_STATUSES = [
  "Available",
  "Loaded to Trolley",
  "Unloaded from Trolley",
];

type ConfirmationMode = "mark" | "revert";

const SupervisorToteInfo = () => {
  const { id: encodedId } = useParams<{ id: string }>();
  const toteId = encodedId ? decodeURIComponent(encodedId) : "";
  const navigate = useNavigate();

  const { data: toteData, isLoading, error, refetch } = useToteById(toteId);
  const { data: priorityOptions } = useTotePriorities();
  const {
    data: trolleyData,
    isLoading: isTrolleyLoading,
    refetch: refetchTrolley,
  } = useTrolleyById(toteData?.trolleyId);

  const updatePriorityMutation = useUpdateTotePriority();

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmationMode, setConfirmationMode] =
    useState<ConfirmationMode>("mark");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [targetPriorityId, setTargetPriorityId] = useState<number | null>(null);
  const [toteRecordId, setToteRecordId] = useState<number | null>(null);
  const [isResolvingRecordId, setIsResolvingRecordId] = useState(false);
  const lastScannedInfo = useMemo(() => {
    if (!trolleyData) {
      return "N/A";
    }

    const locationSegments: string[] = [];

    if (trolleyData.primaryLocation) {
      const locationType = trolleyData.currentLocationType
        ? ` (${trolleyData.currentLocationType})`
        : "";
      locationSegments.push(`${trolleyData.primaryLocation}${locationType}`);
    }

    if (trolleyData.secondaryLocation) {
      locationSegments.push(trolleyData.secondaryLocation);
    }

    if (locationSegments.length > 0 && trolleyData.lastScannedDateTime) {
      const date = new Date(trolleyData.lastScannedDateTime);
      if (!Number.isNaN(date.getTime())) {
        const timePart = date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
        const day = date.toLocaleString("en-GB", { day: "2-digit" });
        const month = date.toLocaleString("en-GB", { month: "short" });
        const year = date.getFullYear();
        const datePart = `${day} ${month}, ${year}`;
        locationSegments.push(`${timePart} - ${datePart}`);
      }
    }

    return locationSegments.length ? locationSegments.join(" • ") : "—";
  }, [trolleyData]);
  useEffect(() => {
    let isMounted = true;

    const resolveRecordId = async () => {
      if (!toteData) {
        setToteRecordId(null);
        return;
      }

      if (typeof toteData.recordId === "number") {
        setToteRecordId(toteData.recordId);
        return;
      }

      setIsResolvingRecordId(true);
      try {
        // Use toteNo (actual tote_id string) instead of id (which might be record ID)
        const toteIdToCheck = toteData.toteNo || toteData.id;
        const response = await api.checkTote({ tote_id: toteIdToCheck });
        const resolvedId = response?.tote_details?.id;
        if (isMounted) {
          setToteRecordId(typeof resolvedId === "number" ? resolvedId : null);
        }
      } catch {
        if (isMounted) {
          setToteRecordId(null);
        }
      } finally {
        if (isMounted) {
          setIsResolvingRecordId(false);
        }
      }
    };

    void resolveRecordId();

    return () => {
      isMounted = false;
    };
  }, [toteData]);

  useEffect(() => {
    if (error && !isLoading && toteId) {
      notification.info({
        message: "Tote Not Found",
        description: `Tote (ID: ${toteId}) isn't registered in our system. Please contact the Operator.`,
        duration: 4,
      });

      const timer = setTimeout(() => {
        navigate("/supervisor-landing");
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error, isLoading, navigate, toteId]);

  const isPriorityActionAllowed =
    toteData && ALLOWED_STATUSES.includes(toteData.activityStatus);

  const priorityLabel = toteData?.priority ? "High" : "Standard";

  const priorityMap = useMemo(() => {
    const map = new Map<string, number>();
    priorityOptions?.forEach((priority) => {
      map.set(priority.priority.toLowerCase(), priority.id);
    });
    return map;
  }, [priorityOptions]);

  const isPriorityDataReady = priorityMap.size > 0;

  const handleBack = () => {
    navigate("/supervisor-landing");
  };

  const openConfirmation = (mode: ConfirmationMode) => {
    if (!toteData || !isPriorityActionAllowed) {
      notification.error({
        message: "Action Not Allowed",
        description: "Tote cannot be prioritised in its current status.",
      });
      return;
    }

    const desiredPriority = mode === "mark" ? "High" : "Standard";
    const priorityId = priorityMap.get(desiredPriority.toLowerCase());

    if (!priorityId) {
      notification.error({
        message: "Priority Not Available",
        description:
          "Unable to resolve the selected priority. Please try again.",
      });
      return;
    }

    setTargetPriorityId(priorityId);
    setConfirmationMode(mode);
    setIsConfirmationOpen(true);
  };

  const handlePriorityConfirm = async () => {
    if (!toteData || targetPriorityId === null) {
      return;
    }

    if (typeof toteRecordId !== "number") {
      notification.error({
        message: "Unable to update priority. Please try again later.",
      });
      return;
    }

    updatePriorityMutation.mutate(
      {
        toteId: toteData.id,
        recordId: toteRecordId,
        priorityId: targetPriorityId,
        trolleyId: toteData.trolleyId,
      },
      {
        onSuccess: (response) => {
          const message =
            response?.message ||
            (confirmationMode === "mark"
              ? "Tote updated to High Priority. The tote and its associated trolley are now prioritised for faster handling and dispatch."
              : "Tote reverted to Standard Priority. The trolley’s priority has been adjusted accordingly.");

          setSuccessMessage(message);
          setIsSuccessOpen(true);
          setIsConfirmationOpen(false);
          setTargetPriorityId(null);
        },
        onError: () => {
          notification.error({
            message: "Unable to update priority. Please try again later.",
          });
          setIsConfirmationOpen(false);
          setTargetPriorityId(null);
        },
      }
    );
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    void refetch();
    if (toteData?.trolleyId) {
      void refetchTrolley();
    }
  };

  const showActionButton =
    Boolean(toteData) && Boolean(isPriorityActionAllowed);
  const actionLabel = toteData?.priority
    ? "Revert Tote to Standard"
    : "Mark Tote as High Priority";

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.contentContainer}>
        <div className={Styles.header}>
          <Icon component={LeftIcon} onClick={handleBack} />
          <div className={Styles.title}>Tote Information</div>
        </div>

        <Spin spinning={isLoading} indicator={<CustomSpin />}>
          {toteData ? (
            <>
              <div className={Styles.infoSection}>
                <h3 className={Styles.sectionTitle}>Tote Info</h3>
                <Card className={Styles.infoCard}>
                  <div className={Styles.toteHeader}>
                    <div className={Styles.toteInfoRow}>
                      <Icon component={ToteIcon} className={Styles.icon} />
                      <div>
                        <div className="labels">Tote No</div>
                        <div className="values">{toteData.toteNo}</div>
                      </div>
                    </div>
                    {toteData.priority && (
                      <div className="priorityTag">
                        <Icon component={PriorityIcon} />
                        <span>High Priority</span>
                      </div>
                    )}
                  </div>

                  <Row gutter={[16, 16]} className={Styles.detailsGrid}>
                    {/* <Col span={12}>
                      <div className="labels">Weight</div>
                      <div className={Styles.detailValue}>
                        {toteData.weight}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="labels">Item count</div>
                      <div className={Styles.detailValue}>
                        {toteData.itemCount}
                      </div>
                    </Col> */}
                    <Col span={12}>
                      <div className="labels">MSN</div>
                      <div className={Styles.detailValue}>
                        {toteData.msn || "—"}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="labels">USM</div>
                      <div className={Styles.detailValue}>
                        {toteData.usn || toteData?.usm || "—"}
                      </div>
                    </Col>

                    <Col span={24}>
                      <div className="labels">Last Scanned Location</div>
                      <div className={Styles.detailValue}>
                        {toteData?.lastScannedLocation}
                      </div>
                    </Col>

                    <Col span={12}>
                      <div className="labels">Status</div>
                      <div
                        className={`${Styles.value} ${Styles.statusValue}`}
                        style={{
                          color: getTrolleyStatus(toteData.activityStatus),
                        }}
                      >
                        {toteData.activityStatus}
                      </div>
                    </Col>
                    {/* <Col span={12}>
                      <div className="labels">Priority Status</div>
                      <div
                        className={`${Styles.value} ${
                          toteData.priority
                            ? Styles.priorityHigh
                            : Styles.priorityStandard
                        }`}
                      >
                        {priorityLabel}
                      </div>
                    </Col> */}

                    <Col span={12}>
                      <div className="labels">Registered Date</div>
                      <div className={Styles.detailValue}>
                        {toteData?.registeredDate
                          ? convertUTCToLocal(toteData?.registeredDate)
                          : "N/A"}
                      </div>
                    </Col>

                    <Col span={12}>
                      <div className="labels">Point of Delivery</div>
                      <div className={Styles.detailValue}>
                        {toteData?.pointOfDelivery || "N/A"}
                      </div>
                    </Col>

                    <Col span={12}>
                      <div className="labels">Date of Delivery</div>
                      <div className={Styles.detailValue}>
                        {toteData?.dateOfDelivery
                          ? convertUTCToLocal(toteData.dateOfDelivery)
                          : "N/A"}
                      </div>
                    </Col>

                    <Col span={12}>
                      <div className="labels">Point of Use / Station</div>
                      <div className={Styles.detailValue}>
                        {toteData?.pointOfUse || "N/A"}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="labels">Date of Use</div>
                      <div className={Styles.detailValue}>
                        {toteData?.dateOfUse
                          ? convertUTCToLocal(toteData.dateOfUse)
                          : "N/A"}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </div>

              <div className={Styles.infoSection}>
                <h3 className={Styles.sectionTitle}>Trolley Info</h3>
                <Card className={Styles.infoCard}>
                  {toteData.trolleyId ? (
                    <Spin
                      spinning={isTrolleyLoading}
                      indicator={<CustomSpin />}
                    >
                      <div className={Styles.toteHeader}>
                        <div className={Styles.toteInfoRow}>
                          <Icon
                            component={TrolleyIcon}
                            className={Styles.icon}
                          />
                          <div>
                            <div className={Styles.label}>Trolley ID</div>
                            <div className={Styles.value}>
                              {toteData.trolleyId}
                            </div>
                          </div>
                        </div>
                        {trolleyData?.priority && (
                          <div className="priorityTag">
                            <Icon component={PriorityIcon} />
                            <span>High Priority</span>
                          </div>
                        )}
                      </div>

                      <div className={Styles.statusPriorityRow}>
                        <Col span={24}>
                          <div className={Styles.label}>Status</div>
                          <div
                            className={`${Styles.value} ${Styles.statusValue}`}
                            style={{
                              color: getTrolleyStatus(
                                trolleyData?.status || "N/A"
                              ),
                            }}
                          >
                            {trolleyData?.status || "N/A"}
                          </div>
                        </Col>

                        <Col span={24}>
                          <div className={Styles.label}>
                            Last Scanned Location
                          </div>
                          <div className={Styles.detailValue}>
                            {lastScannedInfo}
                          </div>
                        </Col>
                      </div>
                    </Spin>
                  ) : (
                    <div className={Styles.emptyCardMessage}>
                      Tote not loaded into any trolley.
                    </div>
                  )}
                </Card>
              </div>
            </>
          ) : (
            !isLoading && (
              <div className={Styles.emptyState}>
                <p>Unable to load tote details.</p>
              </div>
            )
          )}
        </Spin>
      </div>

      {showActionButton && (
        <div className={Styles.bottomAction}>
          <Button
            className={Styles.priorityButton}
            size="large"
            onClick={() =>
              openConfirmation(toteData?.priority ? "revert" : "mark")
            }
            loading={updatePriorityMutation.isPending}
            disabled={
              updatePriorityMutation.isPending ||
              !isPriorityDataReady ||
              typeof toteRecordId !== "number" ||
              isResolvingRecordId
            }
          >
            {actionLabel}
          </Button>
        </div>
      )}

      <PriorityConfirmationModal
        open={isConfirmationOpen}
        mode={confirmationMode}
        toteId={toteData?.toteNo || toteId}
        trolleyId={toteData?.trolleyId}
        onCancel={() => setIsConfirmationOpen(false)}
        onConfirm={handlePriorityConfirm}
        isLoading={updatePriorityMutation.isPending}
      />

      <SuccessAlert
        open={isSuccessOpen}
        title={
          confirmationMode === "mark"
            ? "Tote updated to High Priority"
            : "Tote reverted to Standard Priority"
        }
        message={successMessage}
        buttonText="Done"
        onClose={handleSuccessClose}
      />
    </div>
  );
};

export default SupervisorToteInfo;
